import { MoreHorizontal, RotateCcw, User } from 'lucide-react'
import { type Column, DataTable, type TableState } from '@/components/blocks/data-table'
import { Button } from '@/components/ui/Button'
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/DropdownMenu'
import { cn } from '@/lib/utils'
import { AvatarCell } from '@/components/shared'
import { count } from '@/lib'
import type { LeaderboardRow } from '@/models'

/* Podium tints. Gold/silver/bronze are game colour, so they are tokens defined
 * in the app's own @theme rather than raw palette classes — and they are picked
 * for contrast on a white row, which `slate-300` was not. */
const MEDALS: Record<number, { emoji: string; className: string }> = {
  1: { emoji: '🥇', className: 'border-medal-gold/40 bg-medal-gold/10 text-medal-gold' },
  2: { emoji: '🥈', className: 'border-medal-silver/40 bg-medal-silver/10 text-medal-silver' },
  3: { emoji: '🥉', className: 'border-medal-bronze/40 bg-medal-bronze/10 text-medal-bronze' },
}

export const LEADERBOARD_COLUMNS: Column<LeaderboardRow>[] = [
  {
    key: 'rank',
    header: 'Rank',
    width: '95px',
    sortable: true,
    cell: (row) => {
      const medal = MEDALS[row.rank]
      if (!medal) {
        return <span className="text-fg-subtle tabular-nums">#{row.rank}</span>
      }
      return (
        <span
          className={cn(
            'inline-flex items-center justify-center gap-1 rounded-full border px-2.5 py-0.5 text-sm font-bold tabular-nums',
            medal.className,
          )}
        >
          <span aria-hidden>{medal.emoji}</span> #{row.rank}
        </span>
      )
    },
  },
  {
    key: 'username',
    header: 'Player',
    width: 'minmax(180px,1.4fr)',
    sortable: true,
    cell: (row) => (
      <span className="flex min-w-0 items-center gap-2.5">
        <AvatarCell name={row.username} />
        <span className="text-fg truncate font-semibold">{row.username}</span>
      </span>
    ),
  },
  {
    key: 'clan',
    header: 'Clan',
    width: 'minmax(140px,1fr)',
    sortable: true,
    cell: (row) => <span className="text-fg-muted">{row.clan || '—'}</span>,
  },
  {
    key: 'trophies',
    header: 'Trophies',
    align: 'end',
    sortable: true,
    cell: (row) => (
      <span className="text-fg font-semibold tabular-nums">{count(row.trophies)}</span>
    ),
  },
  {
    key: 'winRate',
    header: 'Win rate',
    align: 'end',
    sortable: true,
    cell: (row) => <span className="text-fg-muted tabular-nums">{row.winRate.toFixed(1)}%</span>,
  },
]

interface LeaderboardTableProps {
  rows: readonly LeaderboardRow[]
  total: number
  state: TableState
  onStateChange: (patch: Partial<TableState>) => void
  onOpen: (row: LeaderboardRow) => void
  onReset: (row: LeaderboardRow) => void
  empty?: React.ReactNode
}

export function LeaderboardTable({
  rows,
  total,
  state,
  onStateChange,
  onOpen,
  onReset,
  empty,
}: LeaderboardTableProps) {
  return (
    <DataTable
      columns={LEADERBOARD_COLUMNS}
      rows={rows}
      total={total}
      state={state}
      onStateChange={onStateChange}
      rowKey={(row) => row.id}
      onRowClick={onOpen}
      empty={empty}
      minWidth={760}
      rowActions={(row) => (
        <DropdownMenu
          trigger={
            <Button variant="ghost" size="icon" aria-label={`Actions for ${row.username}`}>
              <MoreHorizontal />
            </Button>
          }
        >
          <DropdownMenuItem onClick={() => onOpen(row)}>
            <User />
            View Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onClick={() => onReset(row)}>
            <RotateCcw />
            Reset Trophies
          </DropdownMenuItem>
        </DropdownMenu>
      )}
    />
  )
}
